/**
 * Module Staff #08 — Bons de chargement V3
 * Source : Guide_Admin_Baobab_Connect.pdf · Chapitre 12
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const IMG = (name) => `${BACKEND}/api/uploads/docs/html_assets/admin/${name}`;

const moduleStaffBonsChargement = {
  id: 'staff-bons-chargement',
  population: 'staff',
  order: 8,
  category: 'Opérations',
  title: 'Bons de chargement V3',
  subtitle: 'Workflow complet V3 : agent terrain, no-agent, QR code, papier',
  icon: 'ClipboardList',
  duration: '25 min',
  level: 'Intermédiaire',
  audience: 'Opérateurs, agents terrain, encadrants',
  slides: [
    { type: 'cover', eyebrow: 'Module 08 — Staff', title: 'Bons de chargement V3', subtitle: 'Maîtriser les 4 workflows : Agent, No-Agent, QR, Papier', icon: 'ClipboardList', duration: '25 min', audience: 'Opérations terrain', level: 'Intermédiaire' },
    { type: 'intro', title: 'Objectifs', intro: 'Le module V3 des bons de chargement (BC) gère 4 scénarios de terrain. Ce module vous prépare à les exécuter sans erreur.', objectives: [
      'Distinguer les 4 workflows : Agent terrain / Sans agent / QR Code / Papier',
      'Créer un BC depuis le back-office avec photos et signatures',
      'Maîtriser la conformité OHADA (minimum 2 photos, NCC, plaque)',
      'Gérer le BD (déchargement) à l\'arrivée',
      'Utiliser le mode hors-ligne pour les zones sans réseau',
      'Lier les BC/BD aux opérations pour traçabilité complète',
    ]},
    { type: 'screenshot', eyebrow: 'Liste BC/BD', title: 'La page Bons signés',
      image: IMG('img_admin_loading_notes.png'),
      callouts: [
        { label: 'Filtres', text: 'Type (BC/BD), statut, source (Spot/Contrat), période, client.', x: 50, y: 30 },
        { label: 'Liste', text: 'Chaque ligne = 1 bon avec n°, type, opération liée, plaque, statut.', x: 50, y: 55 },
        { label: 'Statut signature', text: 'Brouillon (0/2), En cours (1/2), Signé (2/2).', x: 85, y: 55 },
        { label: 'Téléchargement', text: 'PDF directement, signé numériquement.', x: 95, y: 55 },
      ],
    },
    { type: 'concept', eyebrow: '4 workflows', icon: 'BookOpen', title: 'Quel workflow utiliser ?',
      lede: 'Le choix dépend du contexte terrain :',
      bullets: [
        '🟢 Agent Baobab terrain : le BC est créé et signé sur place via l\'app mobile (recommandé)',
        '🟦 Sans agent : le chauffeur ou client signe directement sur sa propre app',
        '🟧 QR Code : signature à distance via lien QR envoyé par SMS',
        '🟥 Papier : exception — saisie back-office a posteriori avec photo du papier scanné',
      ],
      note: 'OHADA exige toujours minimum 2 photos + signatures + NCC + plaque, quel que soit le workflow.',
    },
    { type: 'steps', title: 'Créer un BC avec Agent terrain', steps: [
      { title: 'L\'opération est statut "Prête au chargement"', detail: 'L\'agent reçoit la notification sur son app mobile.' },
      { title: 'Agent ouvre l\'app sur place', detail: 'Géolocalisation vérifiée automatiquement.' },
      { title: 'Saisie marchandise + photos', detail: 'Minimum 2 photos OHADA : avant chargement + après.' },
      { title: 'Capture plaque véhicule', detail: 'Photo lisible + saisie manuelle pour OCR.' },
      { title: 'Signature chauffeur + expéditeur', detail: 'Signature tactile sur écran ; les 2 sont obligatoires.' },
      { title: 'Validation et upload', detail: 'Le PDF signé est généré et lié à l\'opération automatiquement.' },
    ]},
    { type: 'steps', title: 'Mode QR Code (signature à distance)', steps: [
      { title: 'Le chauffeur est sur place, pas l\'expéditeur', detail: 'Cas fréquent dans les entrepôts logistiques.' },
      { title: 'L\'agent génère un lien QR', detail: 'Le code QR encode un lien unique de signature.' },
      { title: 'Envoi du QR par SMS/WhatsApp à l\'expéditeur', detail: 'L\'expéditeur scanne depuis son téléphone.' },
      { title: 'Page de signature web', detail: 'Aperçu des photos + signature tactile.' },
      { title: 'BC complet généré', detail: 'Notification instantanée à l\'agent terrain.' },
    ]},
    { type: 'comparison', title: 'OHADA : conformité vs non-conformité', do: [
      'Minimum 2 photos (avant + après chargement)',
      'NCC du client visible sur le BC PDF',
      'Plaque véhicule lisible (photo + texte)',
      'Signatures des 2 parties + horodatage',
      'Géolocalisation validée pour les agents terrain',
    ], dont: [
      'Pas de BC sans photo (rejet OHADA = facture nulle)',
      'Pas de signature manquante (sauf statut Brouillon)',
      'Pas de plaque illisible (forcer recapture)',
      'Pas de modification après signature (intégrité PDF)',
      'Pas de papier sans scan ni géoloc post-saisie',
    ]},
    { type: 'quiz', title: 'Quiz #1',
      question: 'Le client est absent à l\'arrivée, mais son magasinier réceptionne. Quel workflow utiliser pour le BD ?',
      options: [
        'Le magasinier signe au nom du client',
        'On reporte la livraison',
        'Workflow QR : envoyer le QR au client pour signature à distance dans les 24 h',
        'On annule l\'opération',
      ],
      answerIndex: 2,
      explanation: 'Le QR permet au client de signer depuis n\'importe où (téléphone), sans bloquer le déchargement. Le magasinier signe sa propre réception en interne.',
    },
    { type: 'quiz', title: 'Quiz #2',
      question: 'Un BC est en statut "Signé (2/2)". Que se passe-t-il si vous voulez modifier la marchandise ?',
      options: [
        'Modification libre',
        'Modification impossible — créer un avenant ou annuler le BC et refaire',
        'Modification après accord du client par téléphone',
        'Modification puis nouvelle signature',
      ],
      answerIndex: 1,
      explanation: 'Un BC signé est juridiquement opposable. Toute modification casserait son intégrité OHADA. Procédure officielle : avenant ou annulation + nouveau BC.',
    },
    { type: 'takeaways', title: 'À retenir', items: [
      '4 workflows : Agent / No-Agent / QR / Papier — choisir selon contexte.',
      'OHADA exige toujours : 2 photos min + signatures + NCC + plaque.',
      'BC signé = juridiquement opposable, intégrité préservée.',
      'Mode hors-ligne disponible pour les zones sans réseau.',
      'BD à l\'arrivée = même rigueur que le BC au départ.',
    ]},
    { type: 'cta', title: 'Module 08 terminé !', subtitle: 'Vous gérez les bons en conformité totale.',
      actions: [
        { label: 'Ouvrir les bons signés', href: '/admin/loading-notes' },
        { label: 'Module Loading notes', href: '/docs/admin/training' },
        { label: 'Catalogue', href: '/docs/admin/training' },
      ],
    },
  ],
};

export default moduleStaffBonsChargement;
